import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  Megaphone, Loader2, Rocket, Pause, Play, Check, Ban, Zap, Target,
  Plug, Trash2, Settings2, TriangleAlert, Radio, ExternalLink, TrendingUp, Mail, FlaskConical, Trophy,
} from "lucide-react";

const PLATFORM_META = {
  meta: { label: "Meta Ads", sub: "Facebook + Instagram", color: "text-sky-400", dot: "#38BDF8" },
  google: { label: "Google Ads", sub: "Search + Display", color: "text-amber-400", dot: "#FBBF24" },
  reddit: { label: "Reddit Ads", sub: "Communities + Search", color: "text-orange-400", dot: "#FF4500" },
};

export default function AdStudio() {
  const [cfg, setCfg] = useState(null);
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/ads/config").then((r) => setCfg(r.data)),
      api.get("/ads/campaigns").then((r) => setCamps(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtime(() => load());

  if (loading && !cfg) {
    return <div className="hud-card rounded-lg p-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="adstudio-page">
      <div>
        <Overline>Autonomous Paid Acquisition</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Ad Studio</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">Connect your ad accounts and let the Marketer agent launch campaigns to your landing pages — inside your spend policy, auto-pausing any ad that goes over your cost-per-close target.</p>
      </div>

      {!cfg?.marketer_agent && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="no-marketer-warn">
          <TriangleAlert className="w-4 h-4 text-amber-400 mt-0.5" />
          <div className="text-sm text-slate-300">No active <span className="text-amber-400 font-medium">Marketer</span> (or Seller) agent yet. Create one in <a href="/agents" className="text-cyan-400 underline">Agents Fleet</a> — it runs campaigns under your wallet caps &amp; approval threshold.</div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <MetaConnect cfg={cfg} onChange={load} />
        <RedditConnect cfg={cfg} onChange={load} />
        <GoogleConnect cfg={cfg} onChange={load} />
      </div>

      <AdSettings cfg={cfg} onChange={load} />

      <LaunchPanel cfg={cfg} onLaunched={load} />

      <CampaignList camps={camps} onChange={load} target={cfg?.settings?.cost_per_close_target} />
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input {...props} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
    </div>
  );
}

function MetaConnect({ cfg, onChange }) {
  const connected = cfg?.meta?.connected;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ ad_account_id: "", page_id: "", access_token: "", app_secret: "", app_id: "", country: "US" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ads/connect/meta", f);
      toast.success(`Meta connected: ${r.data.account_name || "account verified"}`);
      setOpen(false); onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect Meta");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Meta ad account?")) return;
    await api.delete("/ads/disconnect/meta"); toast.success("Meta disconnected"); onChange();
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="meta-connect-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: PLATFORM_META.meta.dot }} />
          <span className={`font-display font-bold ${PLATFORM_META.meta.color}`}>Meta Ads</span>
          <span className="text-[11px] text-slate-500">{PLATFORM_META.meta.sub}</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">CONNECTED</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>

      {connected ? (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Account</span><span className="text-slate-200">{cfg.meta.account_name || cfg.meta.ad_account_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Currency</span><span className="text-slate-200 font-mono">{cfg.meta.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Ad account</span><span className="text-slate-400 font-mono text-xs">{cfg.meta.ad_account_id}</span></div>
          <button data-testid="meta-disconnect-btn" onClick={disconnect} className="mt-3 flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
        </div>
      ) : !open ? (
        <button data-testid="meta-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-mono text-xs uppercase tracking-wider hover:bg-sky-500/25 transition-colors"><Plug className="w-4 h-4" /> Connect Meta account</button>
      ) : (
        <div className="space-y-2.5">
          <Field label="Ad Account ID (act_…)" data-testid="meta-adaccount-input" value={f.ad_account_id} onChange={set("ad_account_id")} placeholder="act_1234567890" />
          <Field label="Page ID" data-testid="meta-page-input" value={f.page_id} onChange={set("page_id")} placeholder="1234567890" />
          <Field label="System User Access Token" data-testid="meta-token-input" value={f.access_token} onChange={set("access_token")} placeholder="EAAB…" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="App Secret" data-testid="meta-secret-input" value={f.app_secret} onChange={set("app_secret")} placeholder="(recommended)" />
            <Field label="Country" data-testid="meta-country-input" value={f.country} onChange={set("country")} placeholder="US" />
          </div>
          <p className="text-[10px] text-slate-500">Credentials are stored per-workspace and used server-side only. We verify them with Meta before saving.</p>
          <div className="flex gap-2">
            <button data-testid="meta-connect-submit" onClick={connect} disabled={busy || !f.ad_account_id || !f.page_id || !f.access_token} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-sky-500 text-[#080B11] font-bold text-sm hover:bg-sky-400 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify &amp; connect
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RedditConnect({ cfg, onChange }) {
  const connected = cfg?.reddit?.connected;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ client_id: "", client_secret: "", refresh_token: "", ad_account_id: "", profile_id: "", conversion_pixel_id: "", country: "US" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ads/connect/reddit", f);
      toast.success(`Reddit connected: ${r.data.account_name || "account verified"}`);
      setOpen(false); onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect Reddit");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Reddit ad account?")) return;
    await api.delete("/ads/disconnect/reddit"); toast.success("Reddit disconnected"); onChange();
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="reddit-connect-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: PLATFORM_META.reddit.dot }} />
          <span className={`font-display font-bold ${PLATFORM_META.reddit.color}`}>Reddit Ads</span>
          <span className="text-[11px] text-slate-500">{PLATFORM_META.reddit.sub}</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">CONNECTED</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>

      {connected ? (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Account</span><span className="text-slate-200">{cfg.reddit.account_name || cfg.reddit.ad_account_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Currency</span><span className="text-slate-200 font-mono">{cfg.reddit.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Ad account</span><span className="text-slate-400 font-mono text-xs">{cfg.reddit.ad_account_id}</span></div>
          <button data-testid="reddit-disconnect-btn" onClick={disconnect} className="mt-3 flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
        </div>
      ) : !open ? (
        <button data-testid="reddit-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-orange-500/15 border border-orange-500/40 text-orange-300 font-mono text-xs uppercase tracking-wider hover:bg-orange-500/25 transition-colors"><Plug className="w-4 h-4" /> Connect Reddit account</button>
      ) : (
        <div className="space-y-2.5">
          <Field label="Ad Account ID" data-testid="reddit-adaccount-input" value={f.ad_account_id} onChange={set("ad_account_id")} placeholder="a2_abc123…" />
          <Field label="Profile ID (advertiser profile)" data-testid="reddit-profile-input" value={f.profile_id} onChange={set("profile_id")} placeholder="t2_… / profile id" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="OAuth Client ID" data-testid="reddit-clientid-input" value={f.client_id} onChange={set("client_id")} />
            <Field label="OAuth Client Secret" data-testid="reddit-secret-input" type="password" value={f.client_secret} onChange={set("client_secret")} />
          </div>
          <Field label="Refresh Token" data-testid="reddit-token-input" type="password" value={f.refresh_token} onChange={set("refresh_token")} placeholder="permanent refresh token" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Conversion Pixel ID (optional)" data-testid="reddit-pixel-input" value={f.conversion_pixel_id} onChange={set("conversion_pixel_id")} placeholder="(recommended)" />
            <Field label="Country" data-testid="reddit-country-input" value={f.country} onChange={set("country")} placeholder="US" />
          </div>
          <p className="text-[10px] text-slate-500">Create a Reddit app (web) at reddit.com/prefs/apps with scopes <span className="font-mono">adsread adsedit</span>, then authorize with <span className="font-mono">duration=permanent</span> to get a refresh token. Stored per-workspace, used server-side only.</p>
          <div className="flex gap-2">
            <button data-testid="reddit-connect-submit" onClick={connect} disabled={busy || !f.client_id || !f.client_secret || !f.refresh_token || !f.ad_account_id || !f.profile_id} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-orange-500 text-[#080B11] font-bold text-sm hover:bg-orange-400 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify &amp; connect
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GoogleConnect({ cfg, onChange }) {
  const connected = cfg?.google?.connected;
  const libOk = cfg?.google?.lib_available;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ developer_token: "", client_id: "", client_secret: "", refresh_token: "", customer_id: "", login_customer_id: "", country: "US" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ads/connect/google", f);
      toast.success(r.data.verified ? "Google Ads connected & verified" : "Google Ads saved (verifies once developer token is approved)");
      setOpen(false); onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect Google Ads");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Ads?")) return;
    await api.delete("/ads/disconnect/google"); toast.success("Google Ads disconnected"); onChange();
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="google-connect-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: PLATFORM_META.google.dot }} />
          <span className={`font-display font-bold ${PLATFORM_META.google.color}`}>Google Ads</span>
          <span className="text-[11px] text-slate-500">{PLATFORM_META.google.sub}</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">{cfg.google.verified ? "CONNECTED" : "PENDING"}</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>

      {!libOk && (
        <div className="mb-3 rounded-md border border-amber-500/20 bg-amber-500/5 p-2.5 text-[11px] text-amber-300/90 flex items-start gap-2">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Google needs a developer token that Google must approve (API Center). You can save your credentials now — campaigns activate the moment it's approved.
        </div>
      )}

      {connected ? (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Customer ID</span><span className="text-slate-400 font-mono text-xs">{cfg.google.customer_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Verified</span><span className={cfg.google.verified ? "text-emerald-400" : "text-amber-400"}>{cfg.google.verified ? "Yes" : "Awaiting token approval"}</span></div>
          <button data-testid="google-disconnect-btn" onClick={disconnect} className="mt-3 flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
        </div>
      ) : !open ? (
        <button data-testid="google-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 font-mono text-xs uppercase tracking-wider hover:bg-amber-500/25 transition-colors"><Plug className="w-4 h-4" /> Connect Google Ads</button>
      ) : (
        <div className="space-y-2.5">
          <Field label="Developer Token" data-testid="google-devtoken-input" value={f.developer_token} onChange={set("developer_token")} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="OAuth Client ID" value={f.client_id} onChange={set("client_id")} />
            <Field label="OAuth Client Secret" value={f.client_secret} onChange={set("client_secret")} />
          </div>
          <Field label="Refresh Token" value={f.refresh_token} onChange={set("refresh_token")} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Customer ID (10 digits)" value={f.customer_id} onChange={set("customer_id")} placeholder="1234567890" />
            <Field label="Login Customer ID (MCC)" value={f.login_customer_id} onChange={set("login_customer_id")} placeholder="0987654321" />
          </div>
          <div className="flex gap-2">
            <button data-testid="google-connect-submit" onClick={connect} disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-amber-500 text-[#080B11] font-bold text-sm hover:bg-amber-400 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save credentials
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ on, onChange, label, sub, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)} className="flex items-start gap-3 text-left w-full">
      <span className={`mt-0.5 w-9 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-slate-700"}`}>
        <span className={`w-4 h-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
      </span>
      <span>
        <span className="block text-sm text-slate-200 font-medium">{label}</span>
        <span className="block text-[10px] text-slate-500">{sub}</span>
      </span>
    </button>
  );
}

function AdSettings({ cfg, onChange }) {
  const s = cfg?.settings || {};
  const [cap, setCap] = useState(s.ad_daily_cap ?? 20);
  const [tgt, setTgt] = useState(s.cost_per_close_target ?? 60);
  const [country, setCountry] = useState(s.default_country ?? "US");
  const [autopilot, setAutopilot] = useState(!!s.ad_autopilot_enabled);
  const [apBudget, setApBudget] = useState(s.ad_autopilot_daily_budget ?? 40);
  const [scale, setScale] = useState(!!s.ad_scale_enabled);
  const [maxBudget, setMaxBudget] = useState(s.ad_max_daily_budget ?? 100);
  const [variations, setVariations] = useState(s.ad_variations ?? 3);
  const [report, setReport] = useState(s.ad_growth_report_enabled !== false);
  const [nudge, setNudge] = useState(s.ad_nudge_enabled !== false);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const [nudging, setNudging] = useState(false);
  useEffect(() => {
    const x = cfg?.settings || {};
    setCap(x.ad_daily_cap ?? 20); setTgt(x.cost_per_close_target ?? 60); setCountry(x.default_country ?? "US");
    setAutopilot(!!x.ad_autopilot_enabled); setApBudget(x.ad_autopilot_daily_budget ?? 40);
    setScale(!!x.ad_scale_enabled); setMaxBudget(x.ad_max_daily_budget ?? 100);
    setVariations(x.ad_variations ?? 3); setReport(x.ad_growth_report_enabled !== false);
    setNudge(x.ad_nudge_enabled !== false);
  }, [cfg]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/ads/settings", {
        ad_daily_cap: Number(cap), cost_per_close_target: Number(tgt), default_country: country,
        ad_autopilot_enabled: autopilot, ad_autopilot_daily_budget: Number(apBudget),
        ad_scale_enabled: scale, ad_max_daily_budget: Number(maxBudget),
        ad_variations: Number(variations), ad_growth_report_enabled: report, ad_nudge_enabled: nudge,
      });
      toast.success("Ad policy saved"); onChange();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const r = await api.post("/ads/report/preview");
      toast.success(r.data?.sent_to ? `Test report sent to ${r.data.sent_to}` : "Report generated");
    } catch { toast.error("Could not send report"); } finally { setSending(false); }
  };

  const runNudges = async () => {
    setNudging(true);
    try {
      const r = await api.post("/ads/nudges/run?force=true");
      const n = r.data?.nudged || 0;
      toast[n ? "success" : "info"](n ? `Sent ${n} follow-up(s) to quiet leads` : "No quiet leads to nudge right now");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nudge run failed"); }
    finally { setNudging(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/ads/autopilot/run");
      const n = r.data?.launched || 0;
      toast[n ? "success" : "info"](n ? `Autopilot launched ${n} campaign(s)` : `Autopilot ran — nothing to launch (${r.data?.reason || "up to date"})`);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Autopilot run failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="ad-settings-card">
      <div className="flex items-center gap-2 mb-3"><Settings2 className="w-4 h-4 text-cyan-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Autonomy policy</span></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Daily budget cap / campaign</label>
          <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="ad-cap-input" type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
          <p className="text-[10px] text-slate-500 mt-1">Agent launches on its own up to this. Above it → your approval.</p>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Auto-pause if cost/close &gt;</label>
          <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="ad-target-input" type="number" min="1" value={tgt} onChange={(e) => setTgt(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
          <p className="text-[10px] text-slate-500 mt-1">Losing ads get paused automatically every 5 min.</p>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Default country</label>
          <input data-testid="ad-country-input" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
        <div className="space-y-2.5">
          <Toggle testid="autopilot-toggle" on={autopilot} onChange={setAutopilot} label="Auto-Pilot Marketing"
                  sub="Each day the Marketer agent picks unpromoted landing pages and launches new campaigns on its own." />
          <div className={autopilot ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Total autopilot budget / day</label>
            <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="autopilot-budget-input" type="number" min="1" value={apBudget} onChange={(e) => setApBudget(e.target.value)} className="w-40 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
          </div>
        </div>
        <div className="space-y-2.5">
          <Toggle testid="scaler-toggle" on={scale} onChange={setScale} label="Winning-Ad Scaler"
                  sub="Automatically raises budget +25% on any ad beating your cost/close target (up to the ceiling)." />
          <div className={scale ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Max daily budget / campaign (ceiling)</label>
            <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="max-budget-input" type="number" min="1" value={maxBudget} onChange={(e) => setMaxBudget(e.target.value)} className="w-40 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
        <div className="space-y-2.5">
          <Toggle testid="report-toggle" on={report} onChange={setReport} label="Daily Growth Report"
                  sub="Every morning, email you what the agent launched, scaled and paused overnight." />
          <div className={report ? "" : "opacity-50 pointer-events-none"}>
            <button type="button" data-testid="report-send-btn" onClick={sendReport} disabled={sending} className="flex items-center gap-1.5 text-[11px] font-mono text-cyan-400 hover:text-cyan-300 disabled:opacity-50">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send me a test report now
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">A/B creative variations / campaign</label>
          <select data-testid="variations-select" value={variations} onChange={(e) => setVariations(e.target.value)} className="w-40 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
            {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v} variation{v > 1 ? "s" : ""}</option>)}
          </select>
          <p className="text-[10px] text-slate-500 mt-1">The agent writes this many ads per campaign; Meta shifts budget to the winner automatically.</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-800">
        <Toggle testid="nudge-toggle" on={nudge} onChange={setNudge} label="Lead-to-Sale Nudges"
                sub="When an ad lead goes quiet, the Seller agent auto-emails a friendly follow-up (max 2) to turn clicks into paid closes." />
        <div className={`mt-2 ${nudge ? "" : "opacity-50 pointer-events-none"}`}>
          <button type="button" data-testid="nudge-run-btn" onClick={runNudges} disabled={nudging} className="flex items-center gap-1.5 text-[11px] font-mono text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
            {nudging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Run nudges now
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button data-testid="ad-settings-save" onClick={save} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save policy
        </button>
        <button data-testid="autopilot-run-btn" onClick={runNow} disabled={running || !(cfg?.meta?.connected || cfg?.reddit?.connected)} title={(cfg?.meta?.connected || cfg?.reddit?.connected) ? "" : "Connect Meta or Reddit first"} className="flex items-center gap-2 px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 font-mono text-xs uppercase tracking-wider hover:bg-emerald-500/20 disabled:opacity-40">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Run autopilot now
        </button>
      </div>
    </div>
  );
}

function LaunchPanel({ cfg, onLaunched }) {
  const pages = cfg?.landing_pages || [];
  const [depId, setDepId] = useState(pages[0]?.id || "");
  const [platform, setPlatform] = useState("meta");
  const [budget, setBudget] = useState(cfg?.settings?.ad_daily_cap ?? 20);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!depId && pages[0]) setDepId(pages[0].id); }, [pages]); // eslint-disable-line

  const canMeta = cfg?.meta?.connected;
  const canReddit = cfg?.reddit?.connected;
  const canGoogle = cfg?.google?.connected && cfg?.google?.verified;

  const launch = async () => {
    if (!depId) { toast.error("Pick a landing page"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ads/campaigns/launch", { platform, landing_deployment_id: depId, daily_budget: Number(budget) });
      const st = r.data.status;
      if (st === "active") toast.success("🚀 Campaign launched live by the Marketer agent");
      else if (st === "awaiting_approval") toast.info("Budget above your cap — campaign queued for your approval");
      else toast.warning(`Campaign ${st}`);
      onLaunched();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Launch failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="launch-panel">
      <div className="flex items-center gap-2 mb-3"><Zap className="w-4 h-4 text-emerald-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Launch a campaign</span></div>
      {pages.length === 0 ? (
        <p className="text-sm text-slate-500">No ad landing pages yet. Your Builder agent (Forge) provisions them — check <a href="/deployments" className="text-cyan-400 underline">Deployments</a>.</p>
      ) : (
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Landing page</label>
            <select data-testid="launch-page-select" value={depId} onChange={(e) => setDepId(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
              {pages.map((p) => <option key={p.id} value={p.id}>{p.product_name}</option>)}
            </select>
            {pages.find((p) => p.id === depId)?.learned_headline && (
              <p className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1" data-testid="learned-winner">
                <Trophy className="w-3 h-3" /> Leading with proven winner: "{pages.find((p) => p.id === depId).learned_headline}"
              </p>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Platform</label>
            <select data-testid="launch-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
              <option value="meta" disabled={!canMeta}>{canMeta ? "Meta" : "Meta (connect first)"}</option>
              <option value="reddit" disabled={!canReddit}>{canReddit ? "Reddit" : "Reddit (connect first)"}</option>
              <option value="google" disabled={!canGoogle}>{canGoogle ? "Google" : "Google (pending)"}</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Daily budget</label>
            <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="launch-budget-input" type="number" min="1" value={budget} onChange={(e) => setBudget(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
          </div>
        </div>
      )}
      {pages.length > 0 && (
        <button data-testid="launch-btn" onClick={launch} disabled={busy || (platform === "meta" && !canMeta) || (platform === "reddit" && !canReddit) || (platform === "google" && !canGoogle)} className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-500 text-[#080B11] font-bold text-sm hover:bg-emerald-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch autonomously
        </button>
      )}
      <p className="text-[10px] text-slate-500 mt-2">The Marketer agent writes the ad copy + creative, targets your country, and books the budget through your spend engine (auto within cap, approval above it).</p>
    </div>
  );
}

function CampaignList({ camps, onChange, target }) {
  const act = async (id, path) => {
    try { await api.post(`/ads/campaigns/${id}/${path}`); onChange(); toast.success("Done"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };
  const resolve = async (id, approve) => {
    try { await api.post(`/ads/campaigns/${id}/resolve`, { approve }); onChange(); toast.success(approve ? "Approved & launched" : "Rejected"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  return (
    <div className="hud-card rounded-lg overflow-hidden" data-testid="campaign-list">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-800"><Megaphone className="w-4 h-4 text-cyan-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Campaigns</span></div>
      {camps.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm">No campaigns yet. Connect an ad account and launch one above.</div>
      ) : (
        <div className="divide-y divide-slate-800/60">
          {camps.map((c) => {
            const spend = c.last_insights?.spend ?? 0;
            const cpc = c.cost_per_close;
            const over = target && cpc != null && cpc > target;
            return (
              <div key={c.id} className="px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`campaign-${c.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-200">{c.product_name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${c.platform === "meta" ? "bg-sky-500/10 text-sky-400" : c.platform === "reddit" ? "bg-orange-500/10 text-orange-400" : "bg-amber-500/10 text-amber-400"}`}>{c.platform}</span>
                    <StatusPill status={c.status} autoPaused={c.auto_paused} />
                    {c.launched_by === "autopilot" && <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-[9px] font-mono">AUTOPILOT</span>}
                    {c.auto_scaled && <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[9px] font-mono flex items-center gap-0.5"><TrendingUp className="w-2.5 h-2.5" /> SCALED</span>}
                    {c.variant_count > 1 && <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 border border-violet-500/30 text-[9px] font-mono flex items-center gap-0.5"><FlaskConical className="w-2.5 h-2.5" /> {c.variant_count} A/B</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1"><Radio className="w-3 h-3 text-emerald-400" /><span className="font-mono text-[10px] text-slate-500">{c.utm_campaign}</span></div>
                </div>
                <div className="text-right"><div className="text-[9px] font-mono uppercase text-slate-500">Budget/day</div><div className="font-mono text-sm text-slate-200">{money(c.daily_budget)}</div></div>
                <div className="text-right"><div className="text-[9px] font-mono uppercase text-slate-500">Spend</div><div className="font-mono text-sm text-slate-200">{money(spend)}</div></div>
                <div className="text-right"><div className="text-[9px] font-mono uppercase text-slate-500">Cost/close</div><div className={`font-mono text-sm ${over ? "text-red-400" : "text-emerald-400"}`}>{cpc != null ? money(cpc) : "—"}</div></div>
                <div className="flex items-center gap-1.5">
                  {c.status === "awaiting_approval" && (
                    <>
                      <button data-testid={`camp-approve-${c.id}`} onClick={() => resolve(c.id, true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase hover:bg-emerald-500/25"><Check className="w-3 h-3" /> Approve</button>
                      <button data-testid={`camp-reject-${c.id}`} onClick={() => resolve(c.id, false)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 font-mono text-[10px] uppercase hover:bg-red-500/25"><Ban className="w-3 h-3" /> Reject</button>
                    </>
                  )}
                  {c.status === "active" && (
                    <button data-testid={`camp-pause-${c.id}`} onClick={() => act(c.id, "pause")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase hover:bg-amber-500/20"><Pause className="w-3 h-3" /> Pause</button>
                  )}
                  {c.status === "paused" && c.platform !== "google" && (
                    <button data-testid={`camp-resume-${c.id}`} onClick={() => act(c.id, "resume")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 font-mono text-[10px] uppercase hover:bg-emerald-500/20"><Play className="w-3 h-3" /> Resume</button>
                  )}
                  {c.landing_url && <a href={c.landing_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-cyan-400"><ExternalLink className="w-3 h-3" /></a>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, autoPaused }) {
  const map = {
    active: ["bg-emerald-500/10 text-emerald-400 border-emerald-500/30", "LIVE"],
    awaiting_approval: ["bg-amber-500/10 text-amber-400 border-amber-500/30", "NEEDS APPROVAL"],
    paused: ["bg-slate-600/20 text-slate-400 border-slate-600/40", autoPaused ? "AUTO-PAUSED" : "PAUSED"],
    rejected: ["bg-red-500/10 text-red-400 border-red-500/30", "REJECTED"],
    blocked: ["bg-red-500/10 text-red-400 border-red-500/30", "BLOCKED"],
  };
  const [cls, label] = map[status] || ["bg-slate-700/40 text-slate-400 border-slate-600/40", status?.toUpperCase()];
  return <span className={`px-1.5 py-0.5 rounded border text-[9px] font-mono ${cls}`}>{label}</span>;
}
